import {
  Badge,
  Group,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { FormSaveBar } from '@/components/FormSaveBar';
import { JURISDICTION_LIST } from '@/lib/constants';
import { useCardForm } from '@/lib/useCardForm';
import {
  apiErrorText,
  type ClientCompanyCard,
  type CompanyUpdate,
  type RequiredFieldKey,
  useUpdateClientCompany,
} from '@/models/clientCompanies';

type Values = {
  name: string;
  legalName: string;
  registrationNumber: string;
  registrationDate: string | null;
  transferDate: string | null;
  jurisdiction: string | null;
  postalCode: string;
  domain: string;
  address: string;
  email: string;
  registryUrl: string;
};

const JURISDICTION_OPTIONS = JURISDICTION_LIST.map(({ code, title }) => ({
  value: code,
  label: `${title} (${code})`,
}));

export function CompanyDataCard({
  card,
  canWrite,
}: {
  card: ClientCompanyCard;
  canWrite: boolean;
}) {
  const { company, requirements } = card;
  const viewOnly = card.readOnly === true;
  const update = useUpdateClientCompany();
  const missing = new Set(
    requirements.fields.filter((f) => !f.filled).map((f) => f.key),
  );
  const { form, dirty, saving, handleSubmit } = useCardForm<Values>({
    initialValues: {
      name: company.name,
      legalName: company.legalName ?? '',
      registrationNumber: company.registrationNumber ?? '',
      registrationDate: company.registrationDate,
      transferDate: company.transferDate,
      jurisdiction: company.jurisdiction,
      postalCode: company.postalCode ?? '',
      domain: company.domain ?? '',
      address: company.address ?? '',
      email: company.email ?? '',
      registryUrl: company.registryUrl ?? '',
    },
    enabled: canWrite,
    save: async (changes) => {
      try {
        await update.mutateAsync({
          id: company.id,
          ...(changes as CompanyUpdate),
        });
      } catch (e) {
        throw new Error(apiErrorText(e));
      }
    },
    successMessage: 'Company data saved',
  });

  const jurisdictionOptions =
    company.jurisdiction &&
    !JURISDICTION_LIST.some((j) => j.code === company.jurisdiction)
      ? [
          { value: company.jurisdiction, label: company.jurisdiction },
          ...JURISDICTION_OPTIONS,
        ]
      : JURISDICTION_OPTIONS;

  const req = (key: RequiredFieldKey) => ({
    withAsterisk: !viewOnly,
    error:
      missing.has(key) && !form.values[key]
        ? 'Required for delivery'
        : undefined,
    readOnly: !canWrite,
  });

  return (
    <CollapsibleCard
      title="Company data"
      rightSection={
        viewOnly ? undefined : missing.size > 0 ? (
          <Badge size="xs" color="orange" variant="light">
            {missing.size} required missing
          </Badge>
        ) : (
          <Badge size="xs" color="green" variant="light">
            Complete
          </Badge>
        )
      }
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="xs">
          <TextInput
            label="Working name"
            size="xs"
            readOnly={!canWrite}
            {...form.getInputProps('name')}
          />
          <TextInput
            label="Full company name"
            size="xs"
            {...form.getInputProps('legalName')}
            {...req('legalName')}
          />
          <Group grow gap="xs" align="flex-start">
            <TextInput
              label="Registration number"
              size="xs"
              {...form.getInputProps('registrationNumber')}
              {...req('registrationNumber')}
            />
            <Select
              label="Jurisdiction"
              size="xs"
              data={jurisdictionOptions}
              searchable
              clearable={canWrite}
              {...form.getInputProps('jurisdiction')}
              {...req('jurisdiction')}
            />
          </Group>
          <Group grow gap="xs" align="flex-start">
            <DateInput
              label="Registration date"
              size="xs"
              valueFormat="DD.MM.YYYY"
              clearable={canWrite}
              {...form.getInputProps('registrationDate')}
              {...req('registrationDate')}
            />
            <DateInput
              label="Transfer date"
              size="xs"
              valueFormat="DD.MM.YYYY"
              clearable={canWrite}
              readOnly={!canWrite}
              {...form.getInputProps('transferDate')}
            />
          </Group>
          <Textarea
            label="Address"
            size="xs"
            autosize
            minRows={1}
            {...form.getInputProps('address')}
            {...req('address')}
          />
          <Group grow gap="xs" align="flex-start">
            <TextInput
              label="Postal code"
              size="xs"
              readOnly={!canWrite}
              {...form.getInputProps('postalCode')}
            />
            <TextInput
              label="Domain"
              size="xs"
              readOnly={!canWrite}
              {...form.getInputProps('domain')}
            />
          </Group>
          <TextInput
            label="Company email"
            size="xs"
            {...form.getInputProps('email')}
            {...req('email')}
          />
          <TextInput
            label="Registry link"
            size="xs"
            placeholder="https://"
            {...form.getInputProps('registryUrl')}
            {...req('registryUrl')}
          />
          {canWrite && dirty && (
            <FormSaveBar saving={saving} onDiscard={form.reset} />
          )}
        </Stack>
      </form>
    </CollapsibleCard>
  );
}
